import { commonApiServices } from "@services/apis/Common";
import { useEffect, useRef, useState } from "react";

interface UploadedFile {
  name: string;
  type: string;
  url?: string;
}

interface UploadFileComponentProps {
  text: string;
  subText?: string;
  onFileUpload?: (file: UploadedFile | null) => void;
  fileId?: string | number;
  value?: string; // URL of previously uploaded file
}

const UploadFileComponent = ({
  text,
  subText,
  onFileUpload,
  fileId,
  value,
}: UploadFileComponentProps) => {
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value && value.trim() !== "") {
      const fileName = value.split("/").pop() || "uploaded-file";
      const fileData = {
        name: fileName,
        type: "application/octet-stream",
        url: value,
      };
      setUploadedFile(fileData);
    } else {
      setUploadedFile(null);
    }
  }, [value]);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);

    try {
      const uid =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");

      const safeFileId = (fileId || "upload")
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-+|-+$/g, "");

      const uniqueKey = `invoice-uploads/${y}${m}${day}/${safeFileId}/${uid}`;

      const uploaded = await commonApiServices.uploadFile(file, uniqueKey);

      const assetUrl =
        (uploaded as any).assetUrl ?? (uploaded as any).data?.assetUrl;
      if (!assetUrl) throw new Error("Upload failed: missing assetUrl.");

      const fileData: UploadedFile = {
        name: file.name,
        type: file.type,
        url: assetUrl,
      };

      setUploadedFile(fileData);
      onFileUpload?.(fileData);
    } catch (error) {
      onFileUpload?.(null);
    } finally {
      setIsUploading(false);
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleViewFile = () => {
    if (uploadedFile?.url) {
      window.open(uploadedFile.url, "_blank");
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-text-base text-[12px] font-medium">{text}</span>
          {subText && (
            <span className="text-text-subtle text-[10px] font-normal">
              {subText}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {uploadedFile && (
            <div
              className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
              onClick={handleViewFile}
            >
              <span className="material-symbols-outlined text-[14px]">
                open_in_new
              </span>
            </div>
          )}
          <div
            className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
            onClick={handleUploadClick}
          >
            <span className="material-symbols-outlined text-[14px]">
              {isUploading ? "hourglass_empty" : "upload"}
            </span>
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        className="hidden"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xlsx,.xls,.txt,.heic"
      />
    </div>
  );
};

export default UploadFileComponent;
